import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Calendar, GitBranch, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";
import { getPackageDisplayName } from "@/domain/packageUtils";

interface ServiceOrderCardProps {
  serviceOrder: ServiceOrderDto;
  onConnectRepo: (serviceOrderId: string) => void;
}

export function ServiceOrderCard({
  serviceOrder,
  onConnectRepo,
}: ServiceOrderCardProps) {
  const navigate = useNavigate();

  const totalSpend = serviceOrder.payments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.amount, 0);

  const formatPaymentModel = (model: string) => {
    switch (model) {
      case "fixed":
        return "Fixed Price";
      case "hourly":
        return "Hourly Rate";
      case "milestone":
        return "Milestone-Based";
      default:
        return model;
    }
  };

  const progressPercent =
    serviceOrder.project.milestones.length > 0
      ? Math.round(
          (serviceOrder.project.milestones.filter(
            (m) => m.developmentStatus === "completed",
          ).length /
            serviceOrder.project.milestones.length) *
            100,
        )
      : 0;

  const hasSourceControl = !!serviceOrder.project.sourceControl?.repo;

  const handleConnectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onConnectRepo(serviceOrder.id);
  };

  return (
    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
      <Card
        className="glass-card border-gradient cursor-pointer hover:border-primary/40 transition-colors h-full"
        onClick={() => navigate(`/service-orders/${serviceOrder.id}/overview`)}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Package className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <span className="truncate block">
                {serviceOrder.customProjectName || serviceOrder.project.name}
              </span>
              <span className="text-xs font-normal text-muted-foreground truncate block">
                {getPackageDisplayName(serviceOrder.packageId)}
              </span>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {serviceOrder.project.scope.description}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Payment Model
              </p>
              <Badge variant="secondary" className="text-xs">
                {formatPaymentModel(serviceOrder.paymentModel)}
              </Badge>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Progress
              </p>
              <p className="text-sm font-semibold text-emerald-400">
                {progressPercent}%
              </p>
            </div>
          </div>

          <div className="pt-3 border-t border-border/50">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="text-center">
                <p className="text-muted-foreground mb-1">Budget</p>
                <p className="font-semibold">
                  ${serviceOrder.financials.budget.toLocaleString()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground mb-1">Spent</p>
                <p className="font-semibold text-primary">
                  ${totalSpend.toLocaleString()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground mb-1">Start</p>
                <p className="font-semibold flex items-center justify-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(serviceOrder.startDate).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                    },
                  )}
                </p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground mb-1">Due</p>
                <p className="font-semibold flex items-center justify-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(serviceOrder.dueDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-border/50">
            {hasSourceControl ? (
              <div className="flex items-center gap-2 text-sm">
                <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Repository</p>
                  <p className="text-sm font-medium truncate">
                    {serviceOrder.project.sourceControl?.repo}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className="text-xs capitalize shrink-0"
                >
                  {serviceOrder.project.sourceControl?.provider}
                </Badge>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={handleConnectClick}
              >
                <GitBranch className="h-4 w-4" />
                Connect to Github
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
